import type { DataActionOptions, MscEnvironments } from '@/types/index'
import type {
  ButtonDataAttributes,
  FreeDownloadButtonAttributes,
  FreeDownloadButtonPosition,
  LazyWithPreload,
  ReactComponentType,
  UserAgentType
} from '@/types/miscellaneous'
import { FINGERPRINT_JS_VERSION_URL, GOOGLE_ADS_URL } from '@/utils/constants'
import storage from '@/utils/storage'
import { lazy } from 'react'


export function lazyWithPreload<T>(
  factory: () => Promise<{ default: ReactComponentType<T> }>
) {
  const Component: LazyWithPreload<T> = Object.assign(
    lazy(factory),
    {
      preload: factory
    }
  )

  return Component
}

export function isString(val: unknown): boolean {
  return typeof val === 'string' || val instanceof String
}

export function isObject(val: unknown): boolean {
  return typeof val === 'object' &&
    val !== null &&
    !Array.isArray(val)
}

export function isDev(): boolean {
  return !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
}

export function roundNumber(number: number, precision = 0): number {
  if (precision === 0) {
    return Math.round(number)
  }

  return Math.round((number + Number.EPSILON) * 10 ** precision) / 10 ** precision
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (!+bytes)
    return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export function android(): boolean {
  return window.navigator.userAgent.toLowerCase().indexOf('android') > -1
}

export function ios(): boolean {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
}
export function mac() {
  return navigator.userAgent.includes('Mac');
}

export function detectUserAgent(): UserAgentType {
  switch(true) {
    case android(): {
      return 'android'
    }
    case ios(): {
      return 'ios'
    }
    case mac(): {
      return 'mac'
    }
    default: {
      return 'windows'
    }
  }
}

/**
 * Returns unique ID based on client browser properties.
 * Loads FingerprintJS dynamically to reduce the size of the bundled output script.
 */
export async function generateFingerprintId(): Promise<string> {
  const FingerprintJS = await import(/* webpackIgnore: true */FINGERPRINT_JS_VERSION_URL)

  const fingerprint = await FingerprintJS.load()
  const { visitorId } = await fingerprint.get()

  return `web://${visitorId}`
}

export async function getDeviceUniqueId(): Promise<string> {
  const cookiesStorage = storage('cookies')
  const deviceId = cookiesStorage.getItem<string>('pushtkn')

  if (!deviceId) {
    const deviceId = await generateFingerprintId()

    cookiesStorage.setItem('pushtkn', deviceId, {
      maxAge: 60 * 60 * 24 * 365 // 1 year
    })

    return deviceId
  }

  return deviceId
}

export function getMscServiceUrl(environment: MscEnvironments): string {
  switch (environment) {
    case 'experimental': return 'https://exp.applications.mobisystems.com/'
    case 'test': return 'https://test.applications.mobisystems.com/'
    case 'production': return 'https://applications.mobisystems.com/'
    default: throw new Error('Provide a valid MSC environment. Options are "experimental", "test", "production"')
  }
}

export function getGooglePagePath(page?: string): string {
  const url = new URL(page ?? window.location.href)

  // truncating if the page has suffix (extension like .html .php)
  const pathname = url.pathname.split('.').slice(0, 1).join('.')

  // trimming '/' at the begging and replacing all '/' with '-'
  return pathname.substring(1).replaceAll('/', '-')
}

export function getFreeDownloadButtonAttributes(
  position: FreeDownloadButtonPosition,
  downloadWinHref?: string
  ): FreeDownloadButtonAttributes {
  const userAgent = detectUserAgent()
  const pagePath = getGooglePagePath()

  const dataLabel = `${pagePath}.button-pdfextra-tool-${position}.${userAgent}`

  if (userAgent === 'android') {
    // android environment
    return {
      href: 'https://play.google.com/store/apps/details?id=com.mobisystems.mobiscanner',
      dataCategory: 'download',
      dataAction: 'android',
      dataLabel
    }
  }

  if (userAgent === 'ios') {
    // ios environment
    return {
      href: 'https://apps.apple.com/us/app/pdf-extra-view-edit-pdfs/id1527395366',
      dataCategory: 'download',
      dataAction: 'ios',
      dataLabel
    }
  }

  // windows environment
  return {
    href: downloadWinHref ?? 'https://mobisystems.com/api/download?platformId=win&product=mobipdf',
    dataCategory: 'download',
    dataAction: 'windows',
    dataLabel
  }
}

export function getButtonDataAttributes(name: string, dataAction: DataActionOptions): ButtonDataAttributes {
  const pagePath = getGooglePagePath()
  const dataCategory = 'tools'
  const dataLabel = `${pagePath}.${name}.${dataAction}`

  return {
    dataCategory,
    dataAction,
    dataLabel
  }
}

export async function detectClientAdBlocker(): Promise<boolean> {
  const request = new Request(GOOGLE_ADS_URL, {
    method: 'HEAD',
    mode: 'no-cors'
  })

  try {
    const response = await fetch(request)

    return response.status !== 0
  } catch {
    return true
  }
}
